import { InformationBox, PublicBackground } from '@app/core';
import { Theme } from '@app/theme';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useParams } from 'react-router-dom';
import { CheckResultLine } from './components/CheckResultLine';
import { LatencyBox } from './components/LatencyBox';
import {
  useCheckDevicesInfo,
  useDevicesInfo,
} from './hooks/useCheckDevicesInfo';
import { SummaryDevices } from '../ParticipationSummary/components/SummaryDevices';
import { useSpeedtest } from './hooks/useSpeedtest';
import { usePublicSession } from '@app/api';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  title: {
    ...theme.fonts.h3,
  },
  requirements: {
    marginTop: theme.marginBase * 4,
  },
  list: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
    marginTop: theme.marginBase * 4,
  },
  resultsLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    gap: theme.marginBase * 2,

    flexWrap: 'wrap',
  },
  paragraph: {
    marginBottom: theme.marginBase * 2,
  },
  bulletList: {
    paddingLeft: theme.marginBase * 4,
  },
  result: {
    margin: 'auto',
    marginBottom: theme.marginBase * 2,
  },
}));

export const TechCheck = () => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: session } = usePublicSession(id);

  const { scores, summary, finished } = useSpeedtest();

  const speedIsOk = useMemo(() => {
    for (const key in scores) {
      if (scores[key].classificationIdx < 3) return false;
    }
    return true;
  }, [scores]);
  const devicesInfo = useCheckDevicesInfo(id);

  return (
    <PublicBackground>
      <h1 className={classes.title}>Vérifications techniques(c'est un POC)</h1>
      <div className={classes.requirements}>
        {session && <SummaryDevices session={session} />}
      </div>
      <ul className={classes.list}>
        <CheckResultLine
          result={speedIsOk}
          loading={!finished}
          text="Vérification du réseau"
        >
          <div className={classes.resultsLine}>
            <LatencyBox
              value={summary?.download}
              color="blue"
              text="download"
              unit="Mbps"
            />
            <LatencyBox
              value={summary?.upload}
              color="purple"
              text="upload"
              unit="Mbps"
            />
            <LatencyBox
              value={summary?.latency}
              color="red"
              text="latency"
              unit="ms"
            />
          </div>
          {scores?.['rtc']?.classificationName && (
            <div className={classes.result}>
              Result : {scores.rtc.classificationName}
            </div>
          )}
          {finished && !speedIsOk && (
            <InformationBox
              variant="danger"
              title="Votre connection est trop juste"
            >
              <div>
                <p className={classes.paragraph}>
                  Vous pouvez essayer de :
                  <ul className={classes.bulletList}>
                    <li>Vous rapprocher de votre box</li>
                    <li>
                      Vous connecter à votre box en utilisant un cable internet
                    </li>
                    <li>Éteindre les autres appareils connectés</li>
                    <li>Mettre à jour votre router</li>
                  </ul>
                </p>
                <p className={classes.paragraph}>
                  Si vous ne parvenez pas à améliorer votre connection, nous ne
                  pourrons malheureusement pas vous laisser participer à l'étude
                  dans ces conditions.
                </p>
              </div>
            </InformationBox>
          )}
        </CheckResultLine>
        <CheckResultLine
          result={devicesInfo?.isValidDevice}
          loading={!devicesInfo?.ready}
          text="Vérification du périphérique"
        >
          {devicesInfo?.ready && !devicesInfo.isValidDevice && (
            <InformationBox
              variant="danger"
              title="Vous n'êtes pas sur le bon type de périphérique"
            >
              <p className={classes.paragraph}>
                Votre périphérique :{' '}
                {t(
                  `application.info.allowedDevicesCategories.${devicesInfo.deviceCategory}`,
                )}
              </p>
            </InformationBox>
          )}
        </CheckResultLine>
        {devicesInfo?.isWebcamRequired && (
          <CheckResultLine
            result={devicesInfo?.isValidWebcam}
            loading={!devicesInfo?.ready}
            text="Vérifications de la webcam"
          />
        )}
        {devicesInfo?.isMicrophoneRequired && (
          <CheckResultLine
            result={devicesInfo?.isValidMicrophone}
            loading={!devicesInfo?.ready}
            text="Vérifications du micro"
          />
        )}
      </ul>
    </PublicBackground>
  );
};
